import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Save, ArrowLeft, FileText, Plus, Trash2, ChevronRight, CheckCircle, XCircle, Upload, FileImage, Eye, ExternalLink } from 'lucide-react';
import { cn } from '../lib/utils';

const PHASE_LABELS = { 1: 'Erfassung', 2: 'Terminierung', 3: 'Auswertung', 4: 'Entscheid' };
const PHASE_COLORS = { 1: 'phase-1', 2: 'phase-2', 3: 'phase-3', 4: 'phase-4' };
const STATUS_COLORS = { open: 'phase-1', accepted: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-700' };
const STATUS_LABELS = { open: 'Offen', accepted: 'Angenommen', rejected: 'Abgelehnt' };

const emptyLine = () => ({ description: '', details: '', quantity: 1, unit: 'Stk.', unit_price: 0, discount_pct: 0, skonto_pct: 0 });

export default function QuoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [quote, setQuote] = useState(null);
  const [form, setForm] = useState({
    title: '', customer_id: '', customer_name: '',
    customer_address: '', customer_email: '', customer_phone: '',
    owner_id: '', owner_name: '', owner_address: '', owner_email: '', owner_phone: '', owner_notes: '',
    property_id: '', property_address: '',
    renter_id: '', renter_name: '', renter_first_name: '', renter_last_name: '', renter_email: '', renter_phone: '',
    object_name: '', cost_limit: '', notes: '', private_notes: '', deadline: '',
    monteur_company: '', monteur_name: '', monteur_address: '', monteur_email: '', monteur_phone: '',
    invoice_lines: []
  });
  const [customers, setCustomers] = useState([]);
  const [owners, setOwners] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [properties, setProperties] = useState([]);
  const [renters, setRenters] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [phaseDlg, setPhaseDlg] = useState(false);
  const [statusDlg, setStatusDlg] = useState(false);
  const [showNewCustomerDetails, setShowNewCustomerDetails] = useState(false);
  const [showNewOwnerDetails, setShowNewOwnerDetails] = useState(false);
  const [showNewRenterDetails, setShowNewRenterDetails] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [ownerSearch, setOwnerSearch] = useState('');
  const [contractorSearch, setContractorSearch] = useState('');
  const [propertySearch, setPropertySearch] = useState('');
  const [renterSearch, setRenterSearch] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    Promise.all([api.get('/customers'), api.get('/owners'), api.get('/contractors'), api.get('/properties'), api.get('/renters')])
      .then(([c, o, ct, p, r]) => { setCustomers(c.data); setOwners(o.data); setContractors(ct.data); setProperties(p.data); setRenters(r.data); });
    if (!isNew) {
      api.get(`/quotes/${id}`)
        .then(res => {
          setQuote(res.data);
          setForm({ ...res.data, invoice_lines: res.data.invoice_lines || [], cost_limit: res.data.cost_limit || '' });
        })
        .catch(() => { toast.error('Offerte nicht gefunden'); navigate('/quotes'); })
        .finally(() => setLoading(false));
    }
  }, [id, isNew, navigate]);

  const computeSubtotal = useCallback((lines) => {
    return lines.reduce((sum, l) => {
      const lt = (l.quantity || 0) * (l.unit_price || 0);
      return sum + lt * (1 - (l.discount_pct || 0) / 100) * (1 - (l.skonto_pct || 0) / 100);
    }, 0);
  }, []);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setLine = (i, k, v) => setForm(f => ({ ...f, invoice_lines: f.invoice_lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, cost_limit: form.cost_limit ? parseFloat(form.cost_limit) : null, invoice_lines: form.invoice_lines.map(l => ({ ...l, quantity: parseFloat(l.quantity) || 1, unit_price: parseFloat(l.unit_price) || 0, discount_pct: parseFloat(l.discount_pct) || 0, skonto_pct: parseFloat(l.skonto_pct) || 0 })) };
      if (isNew) {
        const res = await api.post('/quotes', payload);
        toast.success('Offerte erstellt');
        navigate(`/quotes/${res.data.id}`);
      } else {
        await api.put(`/quotes/${id}`, payload);
        toast.success('Gespeichert');
        const res = await api.get(`/quotes/${id}`);
        setQuote(res.data); setForm({ ...res.data, invoice_lines: res.data.invoice_lines || [], cost_limit: res.data.cost_limit || '' });
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); } finally { setSaving(false); }
  };

  const handlePhaseChange = async (p) => {
    try {
      await api.patch(`/quotes/${id}/phase`, { phase: p });
      const res = await api.get(`/quotes/${id}`);
      setQuote(res.data); setPhaseDlg(false);
      toast.success(`Phase ${p}: ${PHASE_LABELS[p]}`);
    } catch { toast.error('Fehler'); }
  };

  const handleStatus = async (status) => {
    try {
      const res = await api.patch(`/quotes/${id}/status`, { status });
      if (res.data.created_order) {
        toast.success(`Offerte angenommen – Auftrag ${res.data.created_order.order_number} erstellt!`);
        navigate(`/orders/${res.data.created_order.id}`);
      } else {
        toast.success('Offerte abgelehnt');
        setQuote(res.data.quote); setStatusDlg(false);
      }
    } catch { toast.error('Fehler'); }
  };

  const openPdf = async () => {
    try {
      const response = await api.get(`/quotes/${id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      toast.error('PDF konnte nicht geladen werden');
    }
  };

  const uploadFiles = async (files) => {
    const fileList = Array.from(files || []);
    if (!fileList.length || isNew) return;
    setUploading(true);
    try {
      for (const file of fileList) {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`/quotes/${id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(fileList.length === 1 ? 'Datei hochgeladen' : 'Dateien hochgeladen');
      const res = await api.get(`/quotes/${id}`);
      setQuote(res.data);
    } catch {
      toast.error('Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async (e) => {
    await uploadFiles(e.target.files);
    e.target.value = '';
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return <FileImage className="w-3.5 h-3.5 shrink-0 text-blue-500" strokeWidth={1.5} />;
    return <FileText className="w-3.5 h-3.5 shrink-0 text-red-500" strokeWidth={1.5} />;
  };

  const openDocument = (doc) => {
    const url = `data:${doc.content_type};base64,${doc.data}`;
    const win = window.open();
    if (win) win.document.write(`<iframe src="${url}" title="${doc.filename}" style="border:0;width:100%;height:100vh"></iframe>`);
  };

  const subtotal = computeSubtotal(form.invoice_lines || []);
  const matchSearch = (value, term) => (value || '').toString().toLowerCase().includes(term.trim().toLowerCase());
  const filteredCustomers = customers.filter(c => !customerSearch.trim() || [c.name, c.address, c.email, c.phone].some(v => matchSearch(v, customerSearch)));
  const filteredOwners = owners.filter(o => !ownerSearch.trim() || [o.name, o.address, o.email, o.phone, o.notes].some(v => matchSearch(v, ownerSearch)));
  const filteredContractors = contractors.filter(c => !contractorSearch.trim() || [c.company, c.contact_name, c.address, c.email, c.phone, c.specialty, c.notes].some(v => matchSearch(v, contractorSearch)));
  const baseProperties = form.customer_id ? properties.filter(p => p.customer_id === form.customer_id) : properties;
  const filteredProperties = baseProperties.filter(p => !propertySearch.trim() || [p.address, p.object_number, p.description].some(v => matchSearch(v, propertySearch)));
  const baseRenters = form.property_id ? renters.filter(r => r.property_id === form.property_id) : renters;
  const filteredRenters = baseRenters.filter(r => !renterSearch.trim() || [`${r.first_name} ${r.last_name}`, r.email, r.phone, r.address].some(v => matchSearch(v, renterSearch)));

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-5xl" data-testid="quote-detail-page">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate('/quotes')} className="text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading font-black text-2xl truncate">{isNew ? 'Neue Offerte' : (quote?.quote_number || 'Offerte')}</h1>
            {quote && (
              <div className="flex items-center gap-2 mt-1">
                <span className={cn("text-xs font-medium px-2 py-0.5 rounded-sm", PHASE_COLORS[quote.phase])}>Phase {quote.phase} – {PHASE_LABELS[quote.phase]}</span>
                <span className={cn("text-xs font-medium px-2 py-0.5 rounded-sm", STATUS_COLORS[quote.status])}>{STATUS_LABELS[quote.status]}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!isNew && (
              <>
                <Button variant="outline" size="sm" onClick={openPdf} className="rounded-sm gap-2" data-testid="quote-pdf-btn">
                  <FileText className="w-4 h-4" strokeWidth={1.5} />PDF
                </Button>
                {quote?.status === 'open' && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => setPhaseDlg(true)} className="rounded-sm gap-2" data-testid="quote-phase-btn">
                      <ChevronRight className="w-4 h-4" />Phase
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setStatusDlg(true)} className="rounded-sm gap-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="quote-status-btn">
                      <CheckCircle className="w-4 h-4" />Entscheid
                    </Button>
                  </>
                )}
              </>
            )}
            <Button onClick={handleSave} disabled={saving} className="rounded-sm gap-2" data-testid="save-quote-btn">
              <Save className="w-4 h-4" />{saving ? 'Speichern...' : 'Speichern'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-card border border-border/60 rounded-sm p-6 space-y-4">
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground">Offertendaten</h3>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Titel</Label>
                  <Input data-testid="quote-title-input" value={form.title} onChange={e => setField('title', e.target.value)} placeholder="Offertentitel (optional)" className="rounded-sm" />
                </div>

                {/* Verwaltung */}
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Verwaltung wählen</Label>
                      <Input value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} placeholder="Verwaltung suchen..." className="rounded-sm h-8 text-sm" />
                      <Select value={form.customer_id} onValueChange={v => {
                        const cust = customers.find(c => c.id === v);
                        setForm(f => ({ ...f, customer_id: v, customer_name: cust?.name || '', customer_address: cust?.address || '', customer_email: cust?.email || '', customer_phone: cust?.phone || '' }));
                        setShowNewCustomerDetails(false);
                      }}>
                        <SelectTrigger className="rounded-sm" data-testid="quote-customer-select"><SelectValue placeholder="Bestehend..." /></SelectTrigger>
                        <SelectContent>{filteredCustomers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>oder Neue Verwaltung</Label>
                      <Input value={form.customer_name} onChange={e => { setField('customer_name', e.target.value); setField('customer_id', ''); setShowNewCustomerDetails(!!e.target.value); }} placeholder="Name eingeben..." className="rounded-sm" />
                    </div>
                  </div>
                  {showNewCustomerDetails && !form.customer_id && (
                    <div className="grid grid-cols-3 gap-2 bg-muted/50 rounded-sm p-3 border border-border/40">
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">Adresse</Label><Input value={form.customer_address || ''} onChange={e => setField('customer_address', e.target.value)} placeholder="Strasse, PLZ Ort" className="rounded-sm h-8 text-sm" /></div>
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">Telefon</Label><Input value={form.customer_phone || ''} onChange={e => setField('customer_phone', e.target.value)} placeholder="+41 79 ..." className="rounded-sm h-8 text-sm" /></div>
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">E-Mail</Label><Input value={form.customer_email || ''} onChange={e => setField('customer_email', e.target.value)} placeholder="name@mail.ch" className="rounded-sm h-8 text-sm" /></div>
                    </div>
                  )}
                </div>

                {/* Eigentümer */}
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Eigentümer wählen</Label>
                      <Input value={ownerSearch} onChange={e => setOwnerSearch(e.target.value)} placeholder="Eigentümer suchen..." className="rounded-sm h-8 text-sm" />
                      <Select value={form.owner_id || ''} onValueChange={v => {
                        const owner = owners.find(o => o.id === v);
                        setForm(f => ({ ...f, owner_id: v, owner_name: owner?.name || '', owner_address: owner?.address || '', owner_email: owner?.email || '', owner_phone: owner?.phone || '', owner_notes: owner?.notes || '' }));
                        setShowNewOwnerDetails(false);
                      }}>
                        <SelectTrigger className="rounded-sm" data-testid="quote-owner-select"><SelectValue placeholder="Bestehend..." /></SelectTrigger>
                        <SelectContent>{filteredOwners.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>oder Neuer Eigentümer</Label>
                      <Input value={form.owner_name || ''} onChange={e => { setField('owner_name', e.target.value); setField('owner_id', ''); setShowNewOwnerDetails(!!e.target.value); }} placeholder="Name eingeben..." className="rounded-sm" />
                    </div>
                  </div>
                  {(showNewOwnerDetails || (form.owner_id && (form.owner_address || form.owner_email || form.owner_phone || form.owner_notes))) && (
                    <div className="space-y-2 bg-muted/50 rounded-sm p-3 border border-border/40">
                      <div className="grid grid-cols-3 gap-2">
                        <div className="space-y-1"><Label className="text-xs text-muted-foreground">Adresse</Label><Input value={form.owner_address || ''} onChange={e => setField('owner_address', e.target.value)} placeholder="Strasse, PLZ Ort" className="rounded-sm h-8 text-sm" /></div>
                        <div className="space-y-1"><Label className="text-xs text-muted-foreground">Telefon</Label><Input value={form.owner_phone || ''} onChange={e => setField('owner_phone', e.target.value)} placeholder="+41 79 ..." className="rounded-sm h-8 text-sm" /></div>
                        <div className="space-y-1"><Label className="text-xs text-muted-foreground">E-Mail</Label><Input value={form.owner_email || ''} onChange={e => setField('owner_email', e.target.value)} placeholder="name@mail.ch" className="rounded-sm h-8 text-sm" /></div>
                      </div>
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">Notizen</Label><Textarea value={form.owner_notes || ''} onChange={e => setField('owner_notes', e.target.value)} rows={2} className="rounded-sm text-sm resize-y min-h-[56px]" /></div>
                    </div>
                  )}
                </div>

                {/* Liegenschaft & Termin */}
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Liegenschaft</Label>
                      <Input value={propertySearch} onChange={e => setPropertySearch(e.target.value)} placeholder="Liegenschaft suchen..." className="rounded-sm h-8 text-sm" />
                      <Select value={form.property_id} onValueChange={v => { setField('property_id', v); setField('property_address', filteredProperties.find(p => p.id === v)?.address || ''); }}>
                      <SelectTrigger className="rounded-sm" data-testid="quote-property-select"><SelectValue placeholder="Auswählen..." /></SelectTrigger>
                      <SelectContent>{filteredProperties.map(p => <SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>oder Neue Adresse</Label>
                    <Input value={form.property_address || ''} onChange={e => { setField('property_address', e.target.value); setField('property_id', ''); }} placeholder="Adresse eingeben..." className="rounded-sm" />
                  </div>
                </div>

                {/* Mieter */}
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Mieter</Label>
                      <Input value={renterSearch} onChange={e => setRenterSearch(e.target.value)} placeholder="Mieter suchen..." className="rounded-sm h-8 text-sm" />
                      <Select value={form.renter_id} onValueChange={v => {
                        const r = filteredRenters.find(x => x.id === v);
                        setForm(f => ({ ...f, renter_id: v, renter_name: r ? `${r.first_name || ''} ${r.last_name || ''}`.trim() : '', renter_first_name: r?.first_name || '', renter_last_name: r?.last_name || '', renter_email: r?.email || '', renter_phone: r?.phone || '' }));
                        setShowNewRenterDetails(false);
                      }}>
                        <SelectTrigger className="rounded-sm"><SelectValue placeholder="Auswählen..." /></SelectTrigger>
                        <SelectContent>{filteredRenters.map(r => <SelectItem key={r.id} value={r.id}>{r.first_name} {r.last_name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>oder Neuer Mieter</Label>
                      <Input value={form.renter_last_name || ''} onChange={e => { setField('renter_last_name', e.target.value); setField('renter_id', ''); setShowNewRenterDetails(!!e.target.value); }} placeholder="Nachname eingeben..." className="rounded-sm" />
                    </div>
                  </div>
                  {showNewRenterDetails && !form.renter_id && (
                    <div className="grid grid-cols-3 gap-2 bg-muted/50 rounded-sm p-3 border border-border/40">
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">Vorname</Label><Input value={form.renter_first_name || ''} onChange={e => setField('renter_first_name', e.target.value)} placeholder="Vorname" className="rounded-sm h-8 text-sm" /></div>
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">Telefon</Label><Input value={form.renter_phone || ''} onChange={e => setField('renter_phone', e.target.value)} placeholder="+41 79 ..." className="rounded-sm h-8 text-sm" /></div>
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">E-Mail</Label><Input value={form.renter_email || ''} onChange={e => setField('renter_email', e.target.value)} placeholder="name@mail.ch" className="rounded-sm h-8 text-sm" /></div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Termin</Label>
                    <Input type="date" value={form.deadline || ''} onChange={e => setField('deadline', e.target.value)} className="rounded-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Objekt</Label>
                    <Input value={form.object_name || ''} onChange={e => setField('object_name', e.target.value)} placeholder="Einheit / Objekt" className="rounded-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Kostendach (CHF)</Label>
                    <Input type="number" value={form.cost_limit} onChange={e => setField('cost_limit', e.target.value)} placeholder="0.00" className="rounded-sm" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Bemerkungen (Auf Dokument sichtbar)</Label>
                  <Textarea value={form.notes || ''} onChange={e => setField('notes', e.target.value)} placeholder="Bemerkungen für den Kunden..." rows={2} className="rounded-sm resize-none" />
                </div>
                <div className="space-y-1.5">
                  <Label>Interne Notizen (Nur für Sie sichtbar)</Label>
                  <Textarea value={form.private_notes || ''} onChange={e => setField('private_notes', e.target.value)} placeholder="Interne Notizen..." rows={2} className="rounded-sm resize-none border-amber-200 focus-visible:ring-amber-500 bg-amber-50/30" />
                </div>
              </div>
            </div>

            <div className="bg-card border border-border/60 rounded-sm p-6 space-y-4">
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground">Monteur / Dienstleister</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Dienstleister auswählen</Label>
                  <Input value={contractorSearch} onChange={e => setContractorSearch(e.target.value)} placeholder="Dienstleister suchen..." className="rounded-sm h-8 text-sm" />
                  <Select value="" onValueChange={v => {
                    const contractor = contractors.find(c => c.id === v);
                    if (!contractor) return;
                    setForm(f => ({
                      ...f,
                      monteur_company: contractor.company || '',
                      monteur_name: contractor.contact_name || '',
                      monteur_address: contractor.address || '',
                      monteur_email: contractor.email || '',
                      monteur_phone: contractor.phone || '',
                    }));
                  }}>
                    <SelectTrigger className="rounded-sm"><SelectValue placeholder="Bestehend..." /></SelectTrigger>
                    <SelectContent>{filteredContractors.map(c => <SelectItem key={c.id} value={c.id}>{[c.company, c.contact_name].filter(Boolean).join(' – ')}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Aktuelle Auswahl</Label>
                  <div className="min-h-[74px] rounded-sm border border-border/60 bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                    <p className="font-medium text-foreground">{form.monteur_company || 'Kein Dienstleister gewählt'}</p>
                    {form.monteur_name && <p>{form.monteur_name}</p>}
                    {form.monteur_email && <p>{form.monteur_email}</p>}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Firma</Label>
                  <Input value={form.monteur_company || ''} onChange={e => setField('monteur_company', e.target.value)} placeholder="Firmenname" className="rounded-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label>Name</Label>
                  <Input value={form.monteur_name || ''} onChange={e => setField('monteur_name', e.target.value)} placeholder="Ansprechperson" className="rounded-sm" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Adresse</Label>
                <Input value={form.monteur_address || ''} onChange={e => setField('monteur_address', e.target.value)} placeholder="Strasse, PLZ Ort" className="rounded-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>E-Mail</Label>
                  <Input type="email" value={form.monteur_email || ''} onChange={e => setField('monteur_email', e.target.value)} placeholder="monteur@firma.ch" className="rounded-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label>Telefon</Label>
                  <Input value={form.monteur_phone || ''} onChange={e => setField('monteur_phone', e.target.value)} placeholder="+41 79 000 00 00" className="rounded-sm" />
                </div>
              </div>
            </div>

            {/* Invoice Lines */}
            <div className="bg-card border border-border/60 rounded-sm p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground">Offerte</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {form.invoice_lines?.length || 0} Positionen · Subtotal CHF {subtotal.toFixed(2)}
                  </p>
                </div>
                {!isNew && (
                  <Button onClick={() => navigate(`/quotes/${id}/document`)} className="rounded-sm gap-2">
                    <ExternalLink className="w-4 h-4" />
                    Offerte öffnen
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Right */}
          <div className="space-y-4">
            {!isNew && (
              <div className="bg-card border border-border/60 rounded-sm p-5">
                <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Fotos & Dokumente</h3>
                <label className="block">
                  <input type="file" className="sr-only" onChange={handleUpload} accept="image/*,.pdf,.doc,.docx" multiple data-testid="quote-file-upload-input" />
                  <div
                    className="border-2 border-dashed border-border rounded-sm p-5 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-all"
                    onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary'); }}
                    onDragLeave={e => e.currentTarget.classList.remove('border-primary')}
                    onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary'); uploadFiles(e.dataTransfer.files); }}
                  >
                    <Upload className="w-6 h-6 text-primary mx-auto mb-2" strokeWidth={1.5} />
                    <p className="text-sm font-medium text-foreground mb-1">
                      {uploading ? 'Wird hochgeladen...' : 'Foto / PDF / Dokument'}
                    </p>
                    <p className="text-xs text-muted-foreground">Bilder, PDF, Word-Dateien hier ablegen oder auswählen</p>
                  </div>
                </label>
                {quote?.documents?.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {quote.documents.map(doc => (
                      <div key={doc.id} className="flex items-center gap-2 text-xs text-muted-foreground p-2 bg-muted rounded-sm hover:bg-muted/80 transition-colors group">
                        {getFileIcon(doc.content_type)}
                        <span className="truncate flex-1">{doc.filename}</span>
                        <button onClick={() => openDocument(doc)} className="opacity-0 group-hover:opacity-100 transition-opacity text-primary hover:text-primary/80" title="Anzeigen">
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {!isNew && quote?.totals && (
              <div className="bg-primary/5 border border-primary/20 rounded-sm p-5">
                <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Offertsumme</h3>
                <div className="space-y-1.5 text-sm font-mono">
                  <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>CHF {quote.totals.subtotal?.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">MwSt</span><span>CHF {quote.totals.mwst_amount?.toFixed(2)}</span></div>
                  <div className="flex justify-between font-bold text-base border-t border-primary/20 pt-1.5"><span>Total</span><span className="text-primary">CHF {quote.totals.total?.toFixed(2)}</span></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={phaseDlg} onOpenChange={setPhaseDlg}>
        <DialogContent data-testid="quote-phase-dialog">
          <DialogHeader><DialogTitle className="font-heading">Phase wechseln</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            {[1,2,3,4].map(p => (
              <button key={p} disabled={p === quote?.phase} onClick={() => handlePhaseChange(p)} data-testid={`quote-phase-btn-${p}`}
                className={cn("w-full flex items-center justify-between px-4 py-3 rounded-sm border text-sm font-medium transition-colors", p === quote?.phase ? "opacity-40 cursor-not-allowed border-border bg-muted" : "border-border hover:border-primary hover:bg-primary/5 cursor-pointer")}>
                <span className={cn("text-xs px-2 py-0.5 rounded-sm", PHASE_COLORS[p])}>Phase {p}</span>
                <span>{PHASE_LABELS[p]}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={statusDlg} onOpenChange={setStatusDlg}>
        <DialogContent data-testid="quote-status-dialog">
          <DialogHeader><DialogTitle className="font-heading">Offerten-Entscheid</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Was ist der Entscheid für Offerte <strong>{quote?.quote_number}</strong>?</p>
          <DialogFooter className="flex gap-2 sm:flex-row flex-col">
            <Button variant="outline" onClick={() => handleStatus('rejected')} className="rounded-sm gap-2 border-red-200 text-red-700 hover:bg-red-50 flex-1" data-testid="reject-quote-btn">
              <XCircle className="w-4 h-4" />Abgelehnt
            </Button>
            <Button onClick={() => handleStatus('accepted')} className="rounded-sm gap-2 bg-emerald-600 hover:bg-emerald-700 flex-1" data-testid="accept-quote-btn">
              <CheckCircle className="w-4 h-4" />Angenommen → Auftrag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
